import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

/**
 * Persisted auditor session metadata. Deliberately excludes the raw access
 * token (CIR-2026-447): the token is the live 30-day bearer credential and must
 * never sit at rest in web storage where an XSS could read it. Only non-secret
 * identity/expiry metadata is serialised to sessionStorage.
 */
type AuditorSession = {
  sessionId: string;
  clientId: string;
  auditorName: string;
  auditorEmail: string;
  expiresAt: string;
};

type AuditorContextType = {
  session: AuditorSession | null;
  /**
   * The raw access token, held in memory only (CIR-2026-447). It is supplied to
   * the per-request auditor data path (the token-resolving RPCs from
   * CIR-2026-445) and is never persisted. It survives in-app navigation but is
   * intentionally lost on a full page reload, forcing re-authentication.
   */
  token: string | null;
  setSession: (session: AuditorSession, token: string) => void;
  clearSession: () => void;
};

const STORAGE_KEY = "auditorSession";

/**
 * Module-scoped in-memory store for the raw token (CIR-2026-447). The auditor
 * portal wraps each route in its own AuditorProvider (App.tsx), so provider
 * component state does not survive navigation; keeping the token at module
 * scope lets it persist across in-app navigation while still being cleared on a
 * full page reload (when this module is re-evaluated) and on logout. It is
 * never written to sessionStorage / localStorage.
 */
let inMemoryToken: string | null = null;

const AuditorContext = createContext<AuditorContextType | undefined>(undefined);

export const useAuditor = () => {
  const context = useContext(AuditorContext);
  if (!context) {
    throw new Error("useAuditor must be used within an AuditorProvider");
  }
  return context;
};

function loadStoredSession(): AuditorSession | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Record<string, unknown>;
    const expiresAt = typeof parsed.expiresAt === "string" ? parsed.expiresAt : null;
    if (!expiresAt || new Date(expiresAt) <= new Date()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Reconstruct metadata-only. A pre-fix blob may still carry a raw `token`
    // field (CIR-2026-447); it is never carried forward, and if present it is
    // scrubbed from storage immediately so lingering credentials do not survive
    // the upgrade.
    const meta: AuditorSession = {
      sessionId: String(parsed.sessionId ?? ""),
      clientId: String(parsed.clientId ?? ""),
      auditorName: String(parsed.auditorName ?? ""),
      auditorEmail: String(parsed.auditorEmail ?? ""),
      expiresAt,
    };
    if (!meta.sessionId || !meta.clientId) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if ("token" in parsed) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
    }
    return meta;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export const AuditorProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSessionState] = useState<AuditorSession | null>(loadStoredSession);
  // Seed from the module-scoped token so an in-app navigation (which remounts a
  // fresh provider per route) keeps the credential; a full reload seeds null.
  const [token, setTokenState] = useState<string | null>(() => inMemoryToken);

  const setSession = useCallback((s: AuditorSession, t: string) => {
    inMemoryToken = t;
    setTokenState(t);
    setSessionState(s);
    // Persist metadata only — never the raw token (CIR-2026-447).
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  }, []);

  const clearSession = useCallback(() => {
    inMemoryToken = null;
    setTokenState(null);
    setSessionState(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ session, token, setSession, clearSession }),
    [session, token, setSession, clearSession]
  );

  return (
    <AuditorContext.Provider value={value}>
      {children}
    </AuditorContext.Provider>
  );
};
